import React, { useEffect, useState } from "react";
import { http, formatError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { Wallet } from "lucide-react";

export default function OwnerPayroll() {
  const today = new Date();
  const [start, setStart] = useState(format(startOfMonth(today), "yyyy-MM-dd"));
  const [end, setEnd] = useState(format(endOfMonth(today), "yyyy-MM-dd"));
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState([]);

  async function loadAll() {
    const [s, st] = await Promise.all([
      http.get(`/ops/payroll/summary?period_start=${start}&period_end=${end}`),
      http.get("/ops/payroll/settings"),
    ]);
    setSummary(s.data);
    setSettings(st.data);
  }
  useEffect(() => { loadAll(); }, [start, end]);

  async function updateSetting(cleanerId, patch) {
    try {
      const s = settings.find((x) => x.id === cleanerId)?.payroll || { pay_type: "per_visit", rate_cents: 3500 };
      await http.put("/ops/payroll/settings", { cleaner_id: cleanerId, ...s, ...patch });
      toast.success("Saved");
      loadAll();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  }

  return (
    <div className="space-y-6" data-testid="owner-payroll">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Payroll</div>
        <h1 className="font-display text-3xl font-bold text-primary">Cleaner Payroll</h1>
      </div>

      <Card className="border-border/60"><CardContent className="p-5">
        <div className="flex items-end gap-3 flex-wrap">
          <div><Label className="text-xs">From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="payroll-from" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="payroll-to" /></div>
          <div className="ml-auto text-right">
            <div className="text-xs uppercase text-muted-foreground">Total owed for period</div>
            <div className="font-display text-3xl font-bold text-primary" data-testid="payroll-total">
              ${((summary?.total_owed_cents || 0) / 100).toLocaleString()}
            </div>
            <div className="text-xs text-muted-foreground">{summary?.total_visits ?? 0} completed visits</div>
          </div>
        </div>
      </CardContent></Card>

      <Card className="border-border/60"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left p-3">Cleaner</th>
              <th className="text-left p-3">Pay type</th>
              <th className="text-right p-3">Rate</th>
              <th className="text-right p-3">Completed visits</th>
              <th className="text-right p-3">Owed</th>
              <th className="text-left p-3">Adjust</th>
            </tr>
          </thead>
          <tbody>
            {(summary?.rows || []).map((r) => {
              const stg = settings.find((s) => s.id === r.cleaner_id)?.payroll;
              return (
                <tr key={r.cleaner_id} className="border-t border-border/60">
                  <td className="p-3 font-medium">{r.cleaner_name}</td>
                  <td className="p-3">
                    <Select value={r.pay_type} onValueChange={(v) => updateSetting(r.cleaner_id, { pay_type: v })}>
                      <SelectTrigger className="w-32 h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="per_visit">Per visit</SelectItem>
                        <SelectItem value="hourly">Hourly</SelectItem>
                        <SelectItem value="commission">Commission %</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-3 text-right text-xs text-muted-foreground">
                    {r.pay_type === "commission" ? `${(r.rate_cents / 100).toFixed(1)}%` : `$${(r.rate_cents / 100).toFixed(2)}`}
                  </td>
                  <td className="p-3 text-right">{r.visits_completed}</td>
                  <td className="p-3 text-right font-semibold">${(r.owed_cents / 100).toLocaleString()}</td>
                  <td className="p-3">
                    <Input type="number" className="w-24 h-8"
                           defaultValue={r.rate_cents}
                           onBlur={(e) => updateSetting(r.cleaner_id, { rate_cents: Number(e.target.value) })}
                           data-testid={`payroll-rate-${r.cleaner_id}`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent></Card>

      <div className="text-xs text-muted-foreground">Rates are in cents. Per-visit: cents per completed visit. Hourly: cents per hour (visits assumed 1.5h each for MVP). Commission: basis points of gross revenue (3000 = 30%).</div>
    </div>
  );
}
